import { useMemo } from 'react'
import { dayLabelFor, dateForDayOfWeek, isToday as isTodayHelper, formatDateMonthDay, type ActiveDayRange } from '@/lib/weekHelpers'
import { resolveMealTitle } from '@/lib/mealTitle'
import { SlotCell } from './SlotCell'
import { adjacentCell } from '@/lib/mealGridOrder'
import { DAY_MEAL_SLOTS } from '@/types/meal-planner'
import type { MealPlanEntry, MealSlot, Recipe } from '@/types/meal-planner'
import type { FamilyMember } from '@/types/family'

export interface WeekGridProps {
  /** The first day shown, on the household's week start (mealWeek.ts). */
  weekStart: Date
  /** Position bounds of the plan's active range (0 = the first day). Days
   *  outside render nothing; their entries stay in the DB untouched. */
  activeRange: ActiveDayRange
  entries: MealPlanEntry[]
  recipesById: Map<string, Recipe>
  /** Household members, for resolving the name chip on a per-person meal. */
  familyMembers: FamilyMember[]
  onPickRecipe: (dayOfWeek: number, slot: MealSlot) => void
  onTypeName: (dayOfWeek: number, slot: MealSlot, title: string) => void
  onLeftoverFromLastNight: (dayOfWeek: number, slot: MealSlot, sourceEntry: MealPlanEntry) => void
  onChangeRecipe: (dayOfWeek: number, slot: MealSlot, entry: MealPlanEntry) => void
  onClear: (entryId: string) => void
  onLeftoverTomorrow: (dayOfWeek: number, entry: MealPlanEntry) => void
  /** Move an entry to a target cell (day + slot); PlanPage handles swap-on-collision. */
  onMoveMeal: (entryId: string, targetDayOfWeek: number, targetSlot: MealSlot) => void
  /** Open the picker to add a per-person variant to a slot ("split"). */
  onAddForMember: (dayOfWeek: number, slot: MealSlot) => void
}

/** The week as a 7-day x 3-slot grid. Pure presentational — all writes
 *  bubble up to PlanPage, which owns useMealPlan. Realtime updates flow
 *  down as new `entries` props; an entry's dayOfWeek is its position from
 *  `weekStart`, so the grid renders in that order with no remapping. */
const labelAt = (weekStart: Date, position: number) => dayLabelFor(dateForDayOfWeek(weekStart, position).getDay())
function skippedLabel(weekStart: Date, from: number, to: number): string {
  return from === to ? labelAt(weekStart, from) : `${labelAt(weekStart, from)} – ${labelAt(weekStart, to)}`
}

export function WeekGrid({
  weekStart, activeRange, entries, recipesById, familyMembers,
  onPickRecipe, onTypeName, onLeftoverFromLastNight,
  onChangeRecipe, onClear, onLeftoverTomorrow, onMoveMeal, onAddForMember,
}: WeekGridProps) {
  const entriesById = useMemo(() => new Map(entries.map(e => [e.id, e])), [entries])
  const memberNameById = useMemo(() => new Map(familyMembers.map(m => [m.id, m.name])), [familyMembers])

  // A slot can hold the shared meal (for_member_id null) AND per-person
  // variants. Group all entries for each (day, slot) instead of keeping only
  // the last one.
  const entriesByDayBySlot = useMemo(() => {
    const m = new Map<number, Map<MealSlot, MealPlanEntry[]>>()
    for (const e of entries) {
      const dayMap = m.get(e.dayOfWeek) ?? new Map<MealSlot, MealPlanEntry[]>()
      const list = dayMap.get(e.slot) ?? []
      list.push(e)
      dayMap.set(e.slot, list)
      m.set(e.dayOfWeek, dayMap)
    }
    return m
  }, [entries])

  const days = useMemo(
    () => Array.from(
      { length: activeRange.lastDay - activeRange.firstDay + 1 },
      (_, i) => activeRange.firstDay + i,
    ),
    [activeRange.firstDay, activeRange.lastDay],
  )

  return (
    <div className="flex-1 min-w-0 space-y-3">
      {activeRange.firstDay > 0 && (
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-neutral-400 px-1">
          {skippedLabel(weekStart, 0, activeRange.firstDay - 1)} · not planned
        </div>
      )}
      {days.map(d => {
        const date = dateForDayOfWeek(weekStart, d)
        const today = isTodayHelper(date)
        const slotMap = entriesByDayBySlot.get(d)
        // Previous night's SHARED dinner drives the "leftovers from last night" offer.
        const prevDinnerList = d > 0 ? entriesByDayBySlot.get(d - 1)?.get('dinner') : undefined
        const prevDinner = prevDinnerList?.find(e => !e.forMemberId) ?? prevDinnerList?.[0]

        return (
          <div
            key={d}
            data-day={d}
            className={`card px-5 py-4 ${today ? 'ring-1 ring-primary-300' : ''}`}
          >
            <div className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-2 ${today ? 'text-primary-600' : 'text-neutral-400'}`}>
              {dayLabelFor(date.getDay())} · {formatDateMonthDay(date).toUpperCase()}{today && ' · TODAY'}
            </div>
            <div>
              {DAY_MEAL_SLOTS.map(slot => {
                const slotEntries = slotMap?.get(slot) ?? []
                const entry = slotEntries.find(e => !e.forMemberId)          // the shared meal
                const memberEntries = slotEntries
                  .filter(e => e.forMemberId)
                  .map(e => ({
                    entry: e,
                    memberName: (e.forMemberId && memberNameById.get(e.forMemberId)) || 'Someone',
                    title: resolveMealTitle(e, entriesById, recipesById),
                  }))
                // Clamp adjacency to the active range — moving a meal onto a
                // hidden day would make it vanish from the grid.
                const inRange = (cell: { dayOfWeek: number; slot: MealSlot } | null) =>
                  cell && cell.dayOfWeek >= activeRange.firstDay && cell.dayOfWeek <= activeRange.lastDay ? cell : null
                const up = inRange(adjacentCell(d, slot, 'up'))
                const down = inRange(adjacentCell(d, slot, 'down'))

                return (
                  <SlotCell
                    key={slot}
                    dayOfWeek={d}
                    dayLabel={dayLabelFor(date.getDay())}
                    slot={slot}
                    entry={entry}
                    title={entry ? resolveMealTitle(entry, entriesById, recipesById) : undefined}
                    memberEntries={memberEntries}
                    canLeftoverTomorrow={slot === 'dinner' && d < activeRange.lastDay}
                    canLeftoverFromLastNight={!entry && memberEntries.length === 0 && prevDinner != null}
                    previousDinnerTitle={prevDinner ? resolveMealTitle(prevDinner, entriesById, recipesById) : undefined}
                    canMoveUp={up != null}
                    canMoveDown={down != null}
                    onChangeRecipe={(target) => onChangeRecipe(d, slot, target ?? entry!)}
                    onClear={(entryId) => onClear(entryId ?? entry?.id ?? '')}
                    onLeftoverTomorrow={() => entry && onLeftoverTomorrow(d, entry)}
                    onPickRecipe={() => onPickRecipe(d, slot)}
                    onTypeName={(t) => onTypeName(d, slot, t)}
                    onLeftoverFromLastNight={() => prevDinner && onLeftoverFromLastNight(d, slot, prevDinner)}
                    onMoveUp={() => { if (entry && up) onMoveMeal(entry.id, up.dayOfWeek, up.slot) }}
                    onMoveDown={() => { if (entry && down) onMoveMeal(entry.id, down.dayOfWeek, down.slot) }}
                    onAddForMember={() => onAddForMember(d, slot)}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
      {activeRange.lastDay < 6 && (
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-neutral-400 px-1">
          {skippedLabel(weekStart, activeRange.lastDay + 1, 6)} · not planned
        </div>
      )}
    </div>
  )
}
